import { AddressInterface } from "../../Interface/AddressInterface";
import { AddressPredictionInterface } from "../../Interface/AddressPredictionInterface";
// import { UserSettingsInterface } from "../../Entity/Entities/Settings/User/UserSettingsInterface";
import Store from "../../Store/Store";
import Environment from "../../Environment/Environment";

class Places {

  private apiKey: any = Environment.get().googlePlacesApiKey;
  private geocoder: any = null;
  private api: any = null;
  private status: any = null;
  protected interval: any = null;

  constructor() {
    // Delay initialization until we fetch user settings from backend.
    this.interval = setInterval(() => {
      //const userSettings = Store.getConfigurator().getState().app.settings.user; // we change logic for address, now we use agency language setting
      const agencySettings = Store.getConfigurator().getState().app.settings.agency;

      if (!agencySettings) {
        return;
      }

      // Load the language with our API key.
      const language: string = agencySettings?.defaultLanguage?.iso;

      this.loadScript(language);
      clearInterval(this.interval);
    }, 100);
  }

  public changeLanguage(language: string) {
    this.loadScript(language);
  }

  public loadScript(language: string): any {
    /*
    |  Default Google Places API does not support language parameter. It only supports when you
    |  load the whole script again. This hack solves this problem. @todo fix properly
    */

    var scripts = document.getElementsByTagName('head')[0].getElementsByTagName('script');
    var i = !scripts.length ? -1 : scripts.length - 1;

    while (i > -1) {
      document.getElementsByTagName('head')[0].removeChild(scripts[i]);
      i--;
    }

    if (i === -1) {
      const element = document.createElement("script");

      element.type = "text/javascript";
      element.async = true;
      element.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places&language=${language}`;
      element.onload = () => {
        this.geocoder = new (window as any).google.maps.Geocoder();
        this.api = new (window as any).google.maps.places.AutocompleteService();
        this.status = (window as any).google.maps.places.PlacesServiceStatus;
      };

      document.getElementsByTagName('head')[0].appendChild(element);
    }
  }

  public predict(address: string): Promise<Array<{ placeId: string; description: string; }>> {
    return new Promise(accept => {
      // We load the script asyncronously
      if (this.api === null) {
        return accept([]);
      }

      if (address?.length === 0) {
        return accept([]);
      }

      this.api.getPlacePredictions({
        input: address
      }, (predictions: AddressPredictionInterface[] , status) => {

        if (status !== this.status.OK) {
          return accept([]);
        }

        const transformed = predictions.map((prediction: AddressPredictionInterface) => {
          return {
            placeId: prediction.place_id,
            description: prediction.description
          };
        });

        return accept(transformed);
      });
    });
  }

  public predictCountry(address: string): Promise<Array<{ placeId: string; description: string; }>> {
    return new Promise(accept => {
      // We load the script asyncronously
      if (this.api === null) {
        return accept([]);
      }

      if (address?.length === 0) {
        return accept([]);
      }

      this.api.getPlacePredictions({
        input: address,
      }, (predictions: AddressPredictionInterface[] , status) => {

        if (status !== this.status.OK) {
          return accept([]);
        }

        const transformed = predictions
        .filter((prediction: AddressPredictionInterface) => {
          return prediction.types.find(item => item === "country")
        })
        .map((prediction: AddressPredictionInterface) => {
          return {
            placeId: prediction.place_id,
            description: prediction.description
          };
        });

        return accept(transformed);
      });
    });
  }

  public getAddress = (placeId: string): Promise<AddressInterface> => {
    return new Promise((accept, reject) => {
      this.geocoder.geocode({
        placeId: placeId
        }, (results, status) => {
        if (status !== this.status.OK) {
          return reject(new Error(`Geocode service status is not OK.`));
        }

        if (results?.length === 0) {
          return reject(new Error(`Geocode service returned nothing.`));
        }

        const result = results[0];
        const address = {
          googlePlaceId: result.place_id || null,
          countryIsoCode: this.getEntity(result, "country") || "NL",
          country: this.getEntity(result, "country", true) || null,
          postalCode: this.getEntity(result, "postal_code") || null,
          administrativeAreaLevel1: this.getEntity(result, "administrative_area_level_1") || null,
          administrativeAreaLevel2: this.getEntity(result, "administrative_area_level_2") || null,
          administrativeAreaLevel3: this.getEntity(result, "administrative_area_level_3") || null,
          administrativeAreaLevel1Long: this.getEntity(result, "administrative_area_level_1", true) || null,
          administrativeAreaLevel2Long: this.getEntity(result, "administrative_area_level_2", true) || null,
          administrativeAreaLevel3Long: this.getEntity(result, "administrative_area_level_3", true) || null,
          route: this.getEntity(result, "route") || null,
          routeLong: this.getEntity(result, "route", true) || null,
          locality: this.getEntity(result, "locality") || null,
          localityLong: this.getEntity(result, "locality", true) || null,
          sublocality: this.getEntity(result, "sublocality") || null,
          sublocalityLong: this.getEntity(result, "sublocality", true) || null,
          sublocalityLevel1: this.getEntity(result, "sublocality_level_1") || null,
          sublocalityLevel2: this.getEntity(result, "sublocality_level_2") || null,
          sublocalityLevel1Long: this.getEntity(result, "sublocality_level_1", true) || null,
          sublocalityLevel2Long: this.getEntity(result, "sublocality_level_2", true) || null,
          formattedAddress: result.formatted_address,
          lat: result.geometry.location.lat(),
          lng: result.geometry.location.lng(),
          streetNumber: this.getEntity(result, "street_number") || null,
        };

        return accept(address);
      });
    });
  }

  private getEntity = (result, target: string, longName: boolean = false): string => {
    const data = result.address_components.filter(component => {
      return component.types.indexOf(target) !== -1;
    }).map(component => {
      if (longName) {
        return component.long_name;
      }

      return component.short_name;
    });

    return data[0];
  }
}

export default new Places();
